import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/ProfilePage.css'; // Import Premium Specific Styles

const ProfilePage = () => {
    const { user, login, logout } = useAuth();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        mobileNumber: ''
    });
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(false); // New state for button success
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // Compute Initials for Avatar
    const getInitials = (firstName, lastName) => {
        return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    };

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                mobileNumber: user.mobileNumber || ''
            });
        }
    }, [user]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSaved(false);
        setShowToast(false);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.put('http://localhost:5050/api/auth/update-profile', formData, {
                headers: {
                    'x-auth-token': token
                }
            });

            // Update user in context and local storage
            login(token, res.data.user);

            // Show Success Button State
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);

            // Show Success Toast
            setToastMessage('Profile updated successfully!');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);

        } catch (err) {
            console.error(err);
            // In a real app we might show an error toast too, using alert for now specific to error
            alert(err.response?.data?.msg || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to logout?')) {
            logout();
            navigate('/login');
        }
    };

    // Helper to get role class
    const getRoleClass = (role) => {
        if (role === 'super-admin') return 'super-admin';
        if (role === 'admin') return 'admin';
        if (role === 'co-admin') return 'admin'; // Re-use admin style or new one
        return 'member';
    };

    const getRoleLabel = (role) => {
        if (role === 'super-admin') return 'Super Admin';
        if (role === 'admin') return 'Administrator';
        if (role === 'co-admin') return 'Co-Admin';
        return 'Member';
    };

    return (
        <div className="profile-page-container">
            <div className="profile-card">
                {/* --- Identity Header --- */}
                <div className="profile-header">
                    <div className="profile-avatar-container">
                        <div className="profile-avatar">
                            {getInitials(formData.firstName, formData.lastName)}
                        </div>
                        <div className={`profile-role-badge ${getRoleClass(user?.role)}`}>
                            {getRoleLabel(user?.role)}
                        </div>
                    </div>
                    <h2 className="profile-name">{formData.firstName} {formData.lastName}</h2>
                    <p className="profile-email">{formData.email}</p>
                </div>

                {/* --- Form Section --- */}
                <div className="profile-form-section">

                    <form onSubmit={handleSubmit}>
                        <div className="profile-form-group">
                            <label className="profile-label">First Name</label>
                            <input
                                type="text"
                                name="firstName"
                                className="profile-input"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="profile-form-group">
                            <label className="profile-label">Last Name</label>
                            <input
                                type="text"
                                name="lastName"
                                className="profile-input"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="profile-form-group">
                            <label className="profile-label">Email Address</label>
                            <div className="profile-input-wrapper">
                                <input
                                    type="email"
                                    name="email"
                                    className="profile-input"
                                    value={formData.email}
                                    disabled
                                    title="Email cannot be changed"
                                />
                                <svg className="input-lock-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                                </svg>
                            </div>
                        </div>

                        <div className="profile-form-group">
                            <label className="profile-label">Mobile Number</label>
                            <input
                                type="tel"
                                name="mobileNumber"
                                className="profile-input"
                                value={formData.mobileNumber}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            className={`profile-btn-save ${saved ? 'btn-success' : ''}`}
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : saved ? 'Saved ✔' : 'Save Profile Changes'}
                        </button>
                    </form>

                    {/* --- Account Info Section --- */}
                    <div className="profile-section-divider"></div>

                    <h3 className="profile-subsection-title">Account Details</h3>
                    <div className="account-info-grid">
                        <div className="info-item">
                            <label>Member Since</label>
                            {/* Mock data for now, ideally user.createdAt */}
                            <span>Dec 2024</span>
                        </div>
                        <div className="info-item">
                            <label>Account Status</label>
                            <span className="text-success">Active</span>
                        </div>
                    </div>

                    {/* --- Security Shortcuts --- */}
                    <h3 className="profile-subsection-title">Security</h3>
                    <div className="security-actions">
                        <div className="btn-security" onClick={() => alert('Change Password feature coming soon')}>
                            <span>Change Password</span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                        </div>
                        <div className="btn-security logout-danger" onClick={handleLogout}>
                            <span>Log Out</span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                        </div>
                    </div>

                </div>
            </div>

            {/* --- Success Toast --- */}
            <div className={`profile-toast ${showToast ? 'show' : ''}`}>
                <div className="toast-icon">✓</div>
                <div className="toast-message">{toastMessage}</div>
            </div>
        </div>
    );
};

export default ProfilePage;
